<template>
    <div v-if="!isLoading && projects.length === 0">
        <small class="m-0">{{ $t('project.dashboard.zeroProjectMessage') }}</small>
    </div>
    <div v-else>
        <div class="dashboard-info">
          <span>{{$t('project.dashboard.labelTotalProjects')}} {{ this.projects.length}}</span>
          <span> | {{$t('project.dashboard.labelDisplayedProjects') }} {{ this.displayedProjectRooms.length}}</span>
          <span v-if="this.filter.length > 0"> | {{$t('project.dashboard.labelSearchHitResults') }} {{ this.projectRooms.length}}</span>
        </div>
        <b-row class="search-wrapper" :gutter="10">
            <b-col cols="6">
                <b-input-group size="sm" class="mb-2">
                    <b-input :placeholder="$t('project.dashboard.searchPlaceholder') + '...'" v-model="filter"></b-input>
                    <b-input-group-append>
                        <span class="input-group-text"><icon name="search"></icon></span>
                    </b-input-group-append>
                </b-input-group>
            </b-col>

            <b-col cols="4"> &nbsp; </b-col>

            <b-col cols="2">
                <b-form-select v-model="sort" @change="changeFilterOrSort">
                    <option
                            v-for="item in options"
                            :key="item.id"
                            :label="item.label"
                            :value="item.value">
                    </option>
                </b-form-select>
            </b-col>

        </b-row>

        <b-card-group v-if="projects.length !== 0" id="infinite-list" style="-webkit-column-count: 1; column-count: 1">
          <div v-for="(project, index) in displayedProjectRooms"
               :key="project.id"
               class="project-wrapper"
               style="flex: 0 0 100%; margin-bottom: 15px;">
              <b-card border-variant="primary"
                      header-bg-variant="primary"
                      header-text-variant="dark"
                      footer-bg-variant="transparent"
                      header-tag="header"
                      footer-tag="footer"
                      align="center"
                      class="shadow-sm">
                <b-row slot="header"
                       class="mb-0">
                  <b-col :cols="userData.isAdmin ? 10 : 12">
                    <p class="bolder" v-if="project.membership">
                      <router-link :key="project.id"
                                   :to="{ name: 'project-home',
                                   params: { projectId: project.id, membership: project.membership }}">
                                    {{ project.name }}
                      </router-link>
                    </p>
                    <p class="bolder" v-else>{{ project.name }}</p>
                  </b-col>

                  <b-col v-show="userData.isAdmin"
                         :cols="userData.isAdmin ? 2 : 0"
                         style="display: flex; justify-content: space-around;">
                    <b-button type="button"
                              class="btn-primary super-admin"
                              @click="showMembersListInModal(project)">
                      {{ $t('project.dashboard.showMembersBtnTitle') }}
                    </b-button>

                    <b-button type="button"
                              class="btn-delete super-admin"
                              variant="danger"
                              @click="deleteProjectRoom(project, index)"
                              >
                      {{ $t('project.dashboard.deleteBtnTitle') }}
                    </b-button>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col :cols="userData.isAdmin ? 10 : 12">
                    <div v-if="project.description.length >= descriptionLimit">
                      <details>
                        <summary>{{ project.description | truncate(descriptionLimit) }} {{ $t('project.dashboard.descriptionSuffix') }}</summary>
                        <p>
                          {{ project.description }}
                        </p>
                      </details>
                    </div>
                    <div v-else>
                      {{ project.description }}
                    </div>
                  </b-col>
                </b-row>
                <div class="owners-container">
                  <span v-if="project.membership === 'OWNER'"
                        class="badge badge-rounded badge-secondary p-2">
                    {{ $t('project.dashboard.labelOwnerBadge') }}
                  </span>
                  <span class="text-info">
                    {{ $t('project.dashboard.labelOwners') }} {{ project.owners.join(', ') }}
                  </span>
                </div>

                <div v-if="(project.unreadMessages && project.unreadMessages > 0) || project.numberOfTasks != null"
                            slot="footer"
                            class="border-0 mp-0">
                    <b-row v-if="project.unreadMessages && project.unreadMessages > 0"
                           class="text-center">
                        <b-col>
                            <small class="mr-1 text-dark">{{ $t('project.dashboard.newMessages') }}</small>
                            <small>{{ project.unreadMessages }}</small>
                        </b-col>
                    </b-row>
                    <hr v-if="project.unreadMessages > 0" class="mt-1 mb-1">
                    <b-row v-if="project.numberOfTasks != null" class="text-center">
                        <b-col cols="6">
                            <small class="mr-1 text-dark">{{ $t('project.dashboard.openTasks') }}</small>
                            <small>{{ project.numberOfTasks }}</small>
                        </b-col>
                        <b-col v-if="project.membership" cols="6">
                            <small class="mr-1 text-dark">{{ $t('project.dashboard.myOpenTasks') }}</small>
                            <small>{{ project.numberOfUsersTasks }}</small>
                        </b-col>
                        <b-col v-else cols="6">
                          <small class="mr-1 text-dark">{{ $t('project.dashboard.noProjectMembership') }}</small>
                        </b-col>
                    </b-row>
                    <hr v-if="project.taskDueDate"
                        class="mt-1 mb-1">
                    <b-row v-if="project.taskDueDate">
                        <b-col class="text-center">
                            <small class="text-dark">{{ $t('project.dashboard.nextTask') }}:</small><br>
                            <span>
                                <i>{{ project.taskSubject }} - </i>
                                <small>{{ project.taskDueDate | formatDate }}</small>
                            </span>
                        </b-col>
                    </b-row>
                </div>
              </b-card>
          </div>

          <members-list-modal :collaborators="projectCollaborators"
                              :projectName="projectName"
                              :projectId="projectId">
          </members-list-modal>

          <infinite-loading :identifier="infiniteId" @infinite="infiniteHandler">
              <div slot="no-more">{{ $t('project.dashboard.scrollFinished') }}</div>
              <div slot="no-results">{{ $t('project.dashboard.scrollNoResult') }}</div>
          </infinite-loading>

        </b-card-group>
    </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import projectsApi from '../api/projects';
import toastr from "toastr";
import i18n from "../i18n.bootstrap";
import ExportContentsDialog from "./Project/Settings/ExportContentsDialog";
import MembersListModal from './MembersListModal.vue';






export default {
    name: 'dashboard',
    components: {
      ExportContentsDialog,
      MembersListModal
    },
    data: function() {
        return {
            filter: '',
            sort: 'name',
            options: [
                { label: this.$t('project.dashboard.projectRoomName'), value: 'name', id: 1},
                { label: this.$t('project.dashboard.myProjectRooms'), value: 'mine', id: 2 }
            ],
            nextItem: 0,
            displayedProjectRooms: [],
            projectRooms: [],
            infiniteId: +new Date(),
            isOwner: false,
            owners: [],
            includeMembers: true,
            includeMessages: true,
            includeTasks: true,
            exportData: null,
            nameOfProjectToArchive: null,
            descriptionLimit: 80,
            projectCollaborators: [],
            projectName: null,
            projectId: null
        }
    },
    watch: {
        filter(){
            this.displayedProjectRooms = [];
            this.nextItem = 0;
            this.infiniteId += 1;
        }
    },
    methods: {
        infiniteHandler($state) {
            this.projectRooms = this.projects.filter((project) => {

                let ownersString = project.owners.toString();
                const escapedFilter = this.filter.replace(/[-[\]{}()|*+?.\\^$#]/g, '\\$&')
                const regexStr = '(' + escapedFilter
                    .replace(/,/g, " ")
                    .replace(/\s+/g, ' ')
                    .trim()
                    .split(/,|\s/)
                    .join("|") + ')';

                const searchRegEx = new RegExp(regexStr, 'gi');
                return project.name.match(searchRegEx) !== null || ownersString.match(searchRegEx) !== null;
            });

            let projectRoomsSorted;
            if(this.sort != 'mine') {
              projectRoomsSorted = this.projectRooms.sort((a, b) => {
                  return a.name.localeCompare(b.name);
              });
            } else {
              projectRoomsSorted = this.projectRooms.sort((a, b) => {
                let aValue = a.membership === 'OWNER' ? 1 : 0;
                let bValue = b.membership === 'OWNER' ? 1 : 0;
                return bValue - aValue;
              })
            }
            if(projectRoomsSorted.length !== 0 && projectRoomsSorted.length > this.nextItem){
                for (let i = 0; i < 10; i++) {
                    if(i < projectRoomsSorted.length && this.displayedProjectRooms.length < projectRoomsSorted.length) {
                        this.displayedProjectRooms.push(projectRoomsSorted[this.nextItem++]);
                    }
                }
                $state.loaded();
            } else {
                $state.complete();
            }

        },
        changeFilterOrSort() {
            this.displayedProjectRooms = [];
            this.nextItem = 0;
            this.infiniteId += 1;
        },
        deleteProjectRoom(project, index) {

          const deleteProjectRoomAction = () => {
            return projectsApi.deleteProjectByAdmin(project.id)
                .then((res) => {
                  if(res) {
                    this.projects.splice(index, 1);
                    this.displayedProjectRooms.splice(index, 1);
                    toastr.success(i18n.t('project.action.successDelete', {projectName: project.name}));
                  }
                })
                .catch(() => toastr.error(i18n.t('project.action.errorDelete', {projectName: project.name})));
          };


          this.$snotify.confirm(i18n.t('project.settings.advanced.deleteMessage'), '', {
            closeOnClick: true,
            backdrop: 0.3,
            buttons: [
              { text: i18n.t('general.yes'), action: deleteProjectRoomAction },
              { text: i18n.t('general.no') }
            ]
          });

        },

        showMembersListInModal(project){
          const { id, name } = project;
          this.$store.dispatch('projects/loadCollaboratorsByProject', {projectId: id})
              .then((response) => {
                let collaborators = response.data;
                collaborators.sort( ( a, b ) => {
                  const membershipOrder = ['OWNER', 'MEMBER', 'GUEST'];

                  const aMemberIndex = membershipOrder.indexOf(a.membership);
                  const bMemberIndex = membershipOrder.indexOf(b.membership);

                  return aMemberIndex - bMemberIndex;
            });

            this.projectCollaborators = collaborators;
            this.projectName = name;
            this.projectId = id;

            this.$root.$emit('bv::show::modal', 'membersListModal');
          });
        },
    },
    computed: {
        ...mapState('system', ['isLoading']),
        ...mapState('projects', ['projects']),
        ...mapGetters({
          userData: 'userData/userData',
        }),
        projectPermissions() {
          if(this.$store.state.projects.currentProject.projectData.role){
            return this.$store.state.projects.currentProject.projectData.role.permissions;
          } else {
            return [];
          }
        },
    },
    created() {
        this.$store.dispatch('system/setLoadingStatus', true);
        this.$store.dispatch('projects/resetStoreModuleData');
        this.$store.dispatch('projects/loadProjectList')
            .then(() => this.$store.dispatch('system/setLoadingStatus', false))
            .catch(() => this.$store.dispatch('system/setLoadingStatus', false));
    }
}
</script>

<style lang="scss" scoped>
    .search-wrapper{
        margin-bottom: 30px;
        .mb-2 {
          height: calc(2.25rem + 2px);
        }
    }

    #infinite-list{
        column-count: 1;
        position: relative;
        overflow-y: auto;
        height: 75vh;
        display: flex;
        flex-direction: row;
    }
    .project-wrapper {
        .card-text {
            color: gray
        }
        &:hover {
            background-color: #eaeaea;
        }
        .super-admin {
          font-size: 0.85rem;
          height: 2.2rem;
        }
    }

    .dashboard-info {
      padding: 0 0 20px 0;
    }

    details {
      summary {
        list-style: none;
      }
      p {
        text-align: left;
      }
    }

    details summary::-webkit-details-marker {
      display: none;
    }

    @keyframes openDetails {
      0% {opacity: 0;}
      100% {opacity: 1;}
    }

    details[open] p{
      animation: openDetails 0.26s ease-in;
      padding-top: 1rem;
      margin-bottom: 0;
    }

    details[open] summary {
      opacity: 0.33;
      color: #123456;
    }

    .bolder {
      font-weight: bolder;
    }

    .owners-container {
      align-self: flex-start;
      display: flex;
      align-items: center;
      padding: 2rem 0 0.85rem 0;

      :first-child {
        margin-right: 0.85rem;
      }
    }

</style>
